import React, { useState, useEffect, useRef } from 'react';
import { Clock, TrendingUp, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { classes } from '../mockData';
import { BOOKING_URL } from '../config/booking';
import mitgliedApi from '../services/mitgliedApiProxy';
import html2canvas from 'html2canvas';

const Classes = () => {
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [schedule, setSchedule] = useState({
    'Montag': [],
    'Dienstag': [],
    'Mittwoch': [],
    'Donnerstag': [],
    'Freitag': [],
    'Samstag': [],
    'Sonntag': [],
  });
  const [loading, setLoading] = useState(true);
  const scheduleRef = useRef(null);
  
  const categories = ['Alle', ...new Set(classes.map(c => c.category))];
  
  const filteredClasses = selectedCategory === 'Alle' 
    ? classes 
    : classes.filter(c => c.category === selectedCategory);

  // Funktion um Farbe basierend auf Kursname zu bestimmen
  const getCourseColor = (courseName) => {
    const name = courseName.toLowerCase();
    if (name.includes('reha')) return 'bg-yellow-500';
    if (name.includes('cycling') || name.includes('jumping')) return 'bg-blue-500';
    if (name.includes('workout') || name.includes('pump') || name.includes('kraft')) return 'bg-red-500';
    if (name.includes('yoga') || name.includes('mobility') || name.includes('stretching') || name.includes('rücken')) return 'bg-green-500';
    if (name.includes('express') || name.includes('bauch')) return 'bg-orange-500';
    return 'bg-gray-500';
  };

  // Funktion um Wochentag aus Datum zu extrahieren
  const getWeekday = (dateString) => {
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const date = new Date(dateString);
    return days[date.getDay()];
  };

  // Kurse von API laden
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        
        // Hole alle Kurse von der API
        const allCourses = await mitgliedApi.getCourses();
        
        console.log('🔍 API Kurse geladen:', allCourses.length, 'Kurse total');
        
        // Hole aktuelle Woche (Montag bis Sonntag)
        const today = new Date();
        const currentDay = today.getDay(); // 0 = Sonntag, 1 = Montag, ..., 6 = Samstag
        
        console.log('📅 Heute:', today.toISOString().split('T')[0], '- Wochentag:', currentDay);
        
        // Berechne wie viele Tage zurück zum letzten Montag
        const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1; // Sonntag = 6 Tage zurück
        
        const monday = new Date(today);
        monday.setDate(today.getDate() - daysFromMonday);
        monday.setHours(0, 0, 0, 0);
        
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        // Formatiere Datum für Vergleich (YYYY-MM-DD)
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startDateStr = formatDate(monday);
        const endDateStr = formatDate(sunday);
        
        console.log('📆 Wochenbereich:', startDateStr, 'bis', endDateStr);

        // Filtere Kurse für aktuelle Woche (nicht storniert + in dieser Woche)
        const weekCourses = allCourses.filter(course => {
          const inRange = course.date >= startDateStr && course.date <= endDateStr;
          return !course.isCanceled && inRange;
        });

        console.log('✅ Kurse dieser Woche:', weekCourses.length);
        console.log('Kurse Details:', weekCourses.map(c => `${c.date} ${c.name}`));

        // Gruppiere Kurse nach Wochentag
        const newSchedule = {
          'Montag': [],
          'Dienstag': [],
          'Mittwoch': [],
          'Donnerstag': [],
          'Freitag': [],
          'Samstag': [],
          'Sonntag': [],
        };

        weekCourses.forEach(course => {
          const weekday = getWeekday(course.date);
          newSchedule[weekday].push({
            id: course.id,
            time: `${course.from.substring(0, 5)} - ${course.to.substring(0, 5)}`,
            name: course.name,
            color: getCourseColor(course.name),
            room: course.room,
            limit: course.limit,
            date: course.date,
          });
        });

        // Sortiere Kurse nach Zeit
        Object.keys(newSchedule).forEach(day => {
          newSchedule[day].sort((a, b) => a.time.localeCompare(b.time));
        });

        console.log('📊 Finaler Schedule:', newSchedule);
        setSchedule(newSchedule);
      } catch (error) {
        console.error('❌ Fehler beim Laden der Kurse:', error);
        console.error('Error Details:', error.response?.data || error.message);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const legend = [
    { color: 'bg-red-500', label: 'Kraft' },
    { color: 'bg-yellow-500', label: 'Reha' },
    { color: 'bg-blue-500', label: 'Cardio' },
    { color: 'bg-green-500', label: 'Wohlbefinden' },
    { color: 'bg-orange-500', label: 'Express' },
  ];

  // Funktion zum Herunterladen des Stundenplans als PNG
  const downloadScheduleAsPNG = async () => {
    if (!scheduleRef.current) return;

    try {
      // Erstelle einen Klon des Elements für den Screenshot
      const clone = scheduleRef.current.cloneNode(true);
      
      // Entferne interaktive Elemente (Buttons zu Divs machen)
      const buttons = clone.querySelectorAll('button');
      buttons.forEach(button => {
        const div = document.createElement('div');
        div.innerHTML = button.innerHTML;
        div.className = button.className;
        div.style.cssText = button.style.cssText;
        button.parentNode.replaceChild(div, button);
      });

      // Füge Klon temporär zum DOM hinzu
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
      });

      // Entferne Klon
      document.body.removeChild(clone);

      // Canvas zu Blob konvertieren
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `stundenplan-${new Date().toISOString().split('T')[0]}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('Fehler beim Erstellen des PNG:', error);
    }
  };

  return (
    <div className="bg-black min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1716307043003-dbe6a5cc496e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBneW18ZW58MHx8fHwxNzYyODY5NDgyfDA&ixlib=rb-4.1.0&q=85"
            alt="Classes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 uppercase">
            Signature Kurse
          </h1>
          <p className="text-xl text-white/90">
            Exklusive Kurse, entwickelt für das Individuum. Angetrieben von der Gemeinschaft.
          </p>
        </div>
      </section>

      {/* Wochenplan */}
      <section className="py-24 bg-white">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-black">Wochenplan</h2>
            <p className="text-xl text-black/70">Aktuelle Woche - Alle Kurse im Überblick</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-xl text-black/50">Kurse werden geladen...</p>
            </div>
          ) : (
            <>
              <div ref={scheduleRef}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
                  {Object.entries(schedule).map(([day, courses]) => (
                    <div key={day} className="bg-white border-2 border-black/10 rounded-lg overflow-hidden">
                      <div className="bg-black text-white text-center py-3 font-bold">
                        {day}
                      </div>
                      <div className="p-4 space-y-2 min-h-[300px]">
                        {courses.length === 0 ? (
                          <p className="text-center text-black/40 text-sm py-8">Keine Kurse</p>
                        ) : (
                          courses.map((course, idx) => (
                            <a
                              key={idx}
                              href={BOOKING_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${course.color} text-white p-3 rounded-lg text-sm w-full block text-left hover:opacity-90 transition-opacity`}
                            >
                              <div className="font-bold mb-1" style={{ color: '#FFFFFF', fontWeight: '700', fontSize: '14px' }}>
                                {course.name}
                              </div>
                              <div className="text-xs" style={{ color: '#FFFFFF', fontSize: '12px' }}>
                                {course.time}
                              </div>
                            </a>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Legende */}
                <div className="bg-black/5 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-black mb-4">Legende:</h3>
                  <div className="flex flex-wrap gap-6">
                    {legend.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded ${item.color}`}></div>
                        <span className="text-black/80 font-medium">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Download Button */}
              <div className="mt-8 flex justify-center">
                <Button
                  onClick={downloadScheduleAsPNG}
                  className="bg-black text-white hover:bg-black/90 px-8 py-6 text-lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Stundenplan runterladen
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white py-8 border-b border-black/10">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-black text-white'
                    : 'bg-black/5 text-black hover:bg-black/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Classes Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-[1920px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-black">Alle Kurse</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredClasses.map((classItem) => (
              <div key={classItem.id} className="group bg-white border border-black/10 overflow-hidden hover:shadow-2xl transition-shadow">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img 
                    src={classItem.image}
                    alt={classItem.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm text-white px-3 py-1 text-xs font-bold">
                    {classItem.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3 text-black">{classItem.name}</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-2 text-black/70">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{classItem.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-black/70">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm">{classItem.intensity}</span>
                    </div>
                  </div>
                  <p className="text-black/70 text-sm mb-6 leading-relaxed">{classItem.description}</p>
                  <Button
                    asChild
                    className="w-full bg-black text-white hover:bg-black/90"
                  >
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                      Kurs buchen
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Classes;
